import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Image as ImageIcon, Pencil, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { companyAPI } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import './CompanySettings.css';

const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const MAX_BYTES = 2 * 1024 * 1024;

const LEGAL_FORMS = [
  '',
  'Einzelunternehmen',
  'e.K. (eingetragener Kaufmann)',
  'GbR (Gesellschaft bürgerlichen Rechts)',
  'OHG (Offene Handelsgesellschaft)',
  'KG (Kommanditgesellschaft)',
  'UG (haftungsbeschränkt)',
  'GmbH',
  'GmbH & Co. KG',
  'AG (Aktiengesellschaft)',
  'Freiberufler',
  'Sonstige',
];

const FIELDS = [
  { key: 'name', label: 'Firma', required: true },
  { key: 'legal_form', label: 'Gesellschaftsform', type: 'select', options: LEGAL_FORMS },
  { key: 'owner', label: 'Inhaber / Geschäftsführer', required: true },
  { key: 'address_line1', label: 'Adresse (Zeile 1)', required: true },
  { key: 'address_line2', label: 'Adresse (Zeile 2)' },
  { key: 'postal_code', label: 'PLZ', required: true },
  { key: 'city', label: 'Ort', required: true },
  { key: 'country', label: 'Land' },
  { key: 'phone', label: 'Telefon' },
  { key: 'email', label: 'E-Mail' },
  { key: 'website', label: 'Webseite' },
  { key: 'tax_number', label: 'Steuernummer' },
  { key: 'vat_id', label: 'USt-IdNr.' },
  { key: 'tax_office', label: 'Finanzamt' },
  { key: 'small_business', label: 'Kleinunternehmer (§19)', type: 'checkbox' },
  { key: 'vat_default_rate', label: 'USt-Satz (%)', type: 'number' },
  { key: 'kleinunternehmer_notice', label: 'Kleinunternehmer-Hinweis', type: 'textarea' },
  { key: 'bank_account_holder', label: 'Kontoinhaber' },
  { key: 'bank_iban', label: 'IBAN' },
  { key: 'bank_bic', label: 'BIC' },
  { key: 'bank_name', label: 'Bank' },
  { key: 'payment_term_days', label: 'Zahlungsziel (Tage)', type: 'number' },
];

const CompanySettings = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await companyAPI.get();
      setCompany(data);
    } catch {
      toast.error('Firmendaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // — Logo handlers —
  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error('Datei zu groß (max. 2 MB)'); return; }
    if (!ACCEPT.split(',').includes(file.type)) { toast.error('Nur JPG, PNG oder WEBP erlaubt'); return; }
    setPreviewFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewFile) return;
    try {
      setUploading(true);
      await companyAPI.uploadLogo(previewFile);
      toast.success('Logo gespeichert');
      setPreview(null);
      setPreviewFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setCacheBuster(Date.now());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Logo wirklich entfernen?')) return;
    try {
      setUploading(true);
      await companyAPI.deleteLogo();
      toast.success('Logo entfernt');
      setCacheBuster(Date.now());
      await load();
    } catch {
      toast.error('Entfernen fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  // — Edit handlers —
  const startEdit = () => {
    setForm({ ...company });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await companyAPI.update(form);
      setCompany(data);
      setEditing(false);
      toast.success('Firmendaten gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (!company) return null;

  const currentLogoUrl = company.logo_url
    ? `${companyAPI.logoUrl()}?t=${cacheBuster}`
    : null;

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Einstellungen</h1>
          <p>Firmendaten und Logo für Rechnungen &amp; Angebote</p>
        </div>
      </div>

      <div className="content-body">
        {/* Logo Card */}
        <div className="card mb-6">
          <div className="card-header"><h2>Firmen-Logo</h2></div>
          <div className="card-body">
            <div className="logo-settings">
              <div className="logo-preview">
                {preview ? (
                  <img src={preview} alt="Vorschau" />
                ) : currentLogoUrl ? (
                  <img src={currentLogoUrl} alt="Firmenlogo" />
                ) : (
                  <div className="logo-placeholder">
                    <ImageIcon size={40} />
                    <span>Noch kein Logo</span>
                  </div>
                )}
              </div>
              <div className="logo-actions">
                <p className="text-muted mb-2">
                  JPG, PNG oder WEBP &middot; max. 2&nbsp;MB. Das Logo erscheint auf
                  allen PDFs, im Seitenmenü und auf der Login-Seite.
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={onPick}
                  className="form-input"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!previewFile || uploading}
                  >
                    <Upload size={14} />
                    Speichern
                  </button>
                  {company.logo_exists && (
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={uploading}
                    >
                      <Trash2 size={14} />
                      Entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Info Card */}
        <div className="card">
          <div className="card-header company-header-row">
            <h2>Firmenangaben</h2>
            {!editing ? (
              <button className="btn btn-secondary" onClick={startEdit}>
                <Pencil size={14} />
                Bearbeiten
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Save size={14} />
                  {saving ? 'Speichert...' : 'Speichern'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  <X size={14} />
                  Abbrechen
                </button>
              </div>
            )}
          </div>
          <div className="card-body">
            {!editing ? (
              /* Read-only view */
              <dl className="company-meta">
                <dt>Firma</dt><dd>{company.name || '—'}</dd>
                <dt>Gesellschaftsform</dt><dd>{company.legal_form || '—'}</dd>
                <dt>Inhaber / GF</dt><dd>{company.owner || '—'}</dd>
                <dt>Adresse</dt>
                <dd>
                  {company.address_line1}
                  {company.address_line2 ? <><br/>{company.address_line2}</> : null}
                  <br/>{company.postal_code} {company.city}
                  <br/>{company.country}
                </dd>
                <dt>Telefon</dt><dd>{company.phone || '—'}</dd>
                <dt>E-Mail</dt><dd>{company.email || '—'}</dd>
                <dt>Web</dt><dd>{company.website || '—'}</dd>
                <dt>Steuernummer</dt><dd>{company.tax_number || '—'}</dd>
                <dt>USt-IdNr.</dt><dd>{company.vat_id || '—'}</dd>
                <dt>Finanzamt</dt><dd>{company.tax_office || '—'}</dd>
                <dt>Kleinunternehmer</dt>
                <dd>{company.small_business ? 'Ja (§19 UStG)' : 'Nein'}</dd>
                {!company.small_business && (
                  <><dt>USt-Satz</dt><dd>{company.vat_default_rate}%</dd></>
                )}
                <dt>IBAN</dt><dd>{company.bank_iban || '—'}</dd>
                <dt>BIC</dt><dd>{company.bank_bic || '—'}</dd>
                <dt>Bank</dt><dd>{company.bank_name || '—'}</dd>
                <dt>Kontoinhaber</dt><dd>{company.bank_account_holder || '—'}</dd>
                <dt>Zahlungsziel</dt><dd>{company.payment_term_days} Tage</dd>
              </dl>
            ) : (
              /* Edit form */
              <div className="company-edit-form">
                {FIELDS.map(({ key, label, type, required }) => (
                  <div className="form-group" key={key}>
                    <label className="form-label">
                      {label}
                      {required && <span className="required">*</span>}
                    </label>
                    {type === 'select' ? (
                      <select
                        className="form-input"
                        value={form[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                      >
                        {(FIELDS.find((f) => f.key === key)?.options || []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt || '— Bitte wählen —'}
                          </option>
                        ))}
                      </select>
                    ) : type === 'checkbox' ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={!!form[key]}
                          onChange={(e) => handleChange(key, e.target.checked)}
                        />
                        <span>{form[key] ? 'Ja (§19 UStG)' : 'Nein'}</span>
                      </label>
                    ) : type === 'textarea' ? (
                      <textarea
                        className="form-input"
                        value={form[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        rows={2}
                      />
                    ) : type === 'number' ? (
                      <input
                        type="number"
                        className="form-input"
                        value={form[key] ?? ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-input"
                        value={form[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        required={required}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
